import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";

const FOCUSABLE = "button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex='-1'])";

export interface DialogProps {
  open: boolean;
  title: string;
  eyebrow?: string;
  description?: string;
  children: ReactNode;
  onClose(): void;
  closeOnBackdrop?: boolean;
  className?: string;
}

export function Dialog({ open, title, eyebrow, description, children, onClose, closeOnBackdrop = true, className = "" }: DialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => {
      const first =
        panelRef.current?.querySelector<HTMLElement>("[data-autofocus]") ??
        panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panelRef.current)?.focus();
    });
    return () => {
      document.body.style.overflow = previousOverflow;
      restoreRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="dialog-backdrop" onMouseDown={(event) => {
      if (closeOnBackdrop && event.target === event.currentTarget) onClose();
    }}>
      <div
        ref={panelRef}
        className={`dialog-panel ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === "Escape") { event.preventDefault(); onClose(); return; }
          if (event.key !== "Tab") return;
          const items = [...(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
          if (items.length === 0) { event.preventDefault(); panelRef.current?.focus(); return; }
          const first = items[0];
          const last = items.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }}
      >
        <header className="dialog-header">
          <div>{eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}<h2 id={titleId}>{title}</h2></div>
          <button className="icon-button" type="button" aria-label="Fechar" onClick={onClose}>×</button>
        </header>
        {description ? <p id={descriptionId} className="dialog-description">{description}</p> : null}
        {children}
      </div>
    </div>
  );
}

interface ConfirmOptions { title: string; description: string; confirmLabel?: string; tone?: "danger" | "primary"; }
interface PendingConfirm extends ConfirmOptions { resolve(value: boolean): void; }
const ConfirmContext = createContext<(options: ConfirmOptions) => Promise<boolean>>(
  async () => false,
);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ ...options, resolve })), []);
  const settle = useCallback((value: boolean) => {
    setPending((current) => { current?.resolve(value); return null; });
  }, []);
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={pending !== null} title={pending?.title ?? "Confirmar ação"} eyebrow="Confirmação" {...(pending?.description ? { description: pending.description } : {})} onClose={() => settle(false)} closeOnBackdrop={false}>
        <div className="dialog-actions">
          <button data-autofocus className="secondary" type="button" onClick={() => settle(false)}>Cancelar</button>
          <button className={pending?.tone === "primary" ? "primary" : "danger"} type="button" onClick={() => settle(true)}>{pending?.confirmLabel ?? "Confirmar"}</button>
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}

interface Toast { id: number; tone: "success" | "error" | "info"; message: string; }
const ToastContext = createContext<((message: string, tone?: Toast["tone"]) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const notify = useCallback((message: string, tone: Toast["tone"] = "success") => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, tone, message }]);
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4200);
  }, []);
  return <ToastContext.Provider value={notify}>{children}<div className="toast-region" role="region" aria-label="Notificações">{toasts.map((toast) => <div className={`toast ${toast.tone}`} role={toast.tone === "error" ? "alert" : "status"} key={toast.id}>{toast.message}<button type="button" aria-label="Fechar notificação" onClick={() => setToasts((current) => current.filter((item) => item.id !== toast.id))}>×</button></div>)}</div></ToastContext.Provider>;
}

export function useToast() {
  const value = useContext(ToastContext);
  if (value === null) throw new Error("ToastProvider ausente");
  return value;
}

export function Drawer({ open, title, children, onClose }: Omit<DialogProps, "description">) {
  return <Dialog open={open} title={title} onClose={onClose} className="drawer-panel">{children}</Dialog>;
}
